import { Pipe, PipeTransform, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Scheduling, TaskIntervalPattern } from 'src/api';
import {
  GridMultiselectFilterV2,
  MultiselectV2SimpleItemSource,
  unique,
  GridSortParamV2,
  MultiselectV2Item,
  asObservable$,
} from '@schenck/rotec-components';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

export function createSchedulingFilter<T extends { scheduling: Scheduling }>(
  tasks: T[] | Observable<T[]>,
  translate: TranslateService
): GridMultiselectFilterV2 {
  const items = new MultiselectV2SimpleItemSource(
    asObservable$(tasks).pipe(map((res) => getTaskItems(res))),
    translate
  );
  return {
    type: 'multiselect',
    withCustomValue: false,
    items,
  };
}

function getTaskItems(tasks: { scheduling: Scheduling }[]): MultiselectV2Item<string>[] {
  return unique(
    (tasks || []).map((task) => getSchedulingTypeAsText(task.scheduling)).filter((val) => !!val)
  ).map((value) => ({
    value,
    name: value,
  }));
}

export function gridSchedulingMultiselectFilter(value: Scheduling | null, searchValue: string[]) {
  return searchValue.includes(getSchedulingTypeAsText(value));
}

export function getSchedulingTypeAsText(value: Scheduling | null): string {
  if (!value) {
    return 'Once';
  }
  switch (value.pattern) {
    case TaskIntervalPattern.Daily:
      return 'Daily';
    case TaskIntervalPattern.Weekly:
      return 'Weekly';
    case TaskIntervalPattern.Monthly:
    case TaskIntervalPattern.MonthlyByWeekDayAppearance:
      return 'Monthly';
    case TaskIntervalPattern.Yearly:
    case TaskIntervalPattern.YearlyByWeekDayAppearance:
      return 'Yearly';
    default:
      return '';
  }
}

type Comparator = (res1: any, res2: any, sort: GridSortParamV2) => number;

const schedulingSortMap: Record<TaskIntervalPattern | 'null', number> = {
  ['null']: -1,
  [TaskIntervalPattern.NotSet]: -1,
  [TaskIntervalPattern.Daily]: 0,
  [TaskIntervalPattern.EveryWorkDay]: 0,
  [TaskIntervalPattern.Weekly]: 1,
  [TaskIntervalPattern.Monthly]: 2,
  [TaskIntervalPattern.MonthlyByWeekDayAppearance]: 2,
  [TaskIntervalPattern.Yearly]: 3,
  [TaskIntervalPattern.YearlyByWeekDayAppearance]: 3,
};

export function enhanceSortWithScheduling(sortComparator: Comparator): Comparator {
  return (res1: any, res2: any, sort: GridSortParamV2) => {
    if (sort.key === 'scheduling') {
      const dir = sort.direction === 'asc' ? 1 : -1;
      return (
        dir *
        (schedulingSortMap[res1.scheduling && res1.scheduling.pattern] -
          schedulingSortMap[res2.scheduling && res2.scheduling.pattern])
      );
    } else {
      return sortComparator(res1, res2, sort);
    }
  };
}

@Pipe({ name: 'taskInterval' })
export class TaskIntervalPipe implements PipeTransform {
  private readonly translate = inject(TranslateService);

  transform(value: Scheduling | null): string {
    const text = getSchedulingTypeAsText(value);
    return text ? this.translate.instant(text) : '';
  }
}
